import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  AiClass,
  AiEvalCase,
  AiEvalRun,
  AiEvalRunDetail,
  AiModel,
  AiAgentLoopMode,
  AiRoutingMode,
  AiRoutingOverview,
  AiTask,
  TokenAnalyticsService,
} from '../../../services/token-analytics.service';

const CLASS_RANK: Record<AiClass, number> = { light: 0, standard: 1, strong: 2, frontier: 3 };

interface CandidateRow {
  model: string;
  backend: string;
  klass: AiClass | null;
  price: AiModel['price'];
  costPerCall: number | null;
  isDefault: boolean;
  isActive: boolean;
  passedRun: string | null;
  canSwitch: boolean;
}

/**
 * KI-Routing (super_admin): welche KI-Aufgabe auf welchem Modell läuft, was sie
 * kostet, und Vergleichsläufe, bevor eine Aufgabe auf ein anderes Modell geht.
 *
 * Backend: al-tutor `/admin/ai-routing` (migration 187, services/ai_routing.py).
 * Eine Umstellung wirkt nur für verdrahtete Aufgaben und verlangt — wo es
 * Testfälle gibt — einen bestandenen Vergleichslauf; der Server prüft beides.
 * Abgeleitete Listen werden in `rebuild()` einmal gebaut, nie per Getter
 * (CLAUDE.md Regel 18).
 */
@Component({
  selector: 'app-ai-routing',
  templateUrl: './ai-routing.component.html',
  styleUrl: './ai-routing.component.scss',
})
export class AiRoutingComponent implements OnInit, OnDestroy {
  data: AiRoutingOverview | null = null;
  isLoading = false;
  loadFailed = false;

  areas: string[] = [];
  area = 'ALL';
  onlyRoutable = false;
  visibleTasks: AiTask[] = [];

  selected: AiTask | null = null;
  candidateRows: CandidateRow[] = [];
  evalPick = new Set<string>();
  repeats = 1;
  /** Which stored cases a run uses: core | all | random:N. */
  caseSet = 'core';
  private readonly functionCaseSets = ['core', 'all', 'random:10', 'random:25'];
  /** Case sets for the selected task — function-level (SPECS) plus replays.
   *  Built in rebuildSelection(), never by a getter (CLAUDE.md Regel 18). */
  caseSets: string[] = [];
  budgetUsd = 1;
  readonly budgets = [0.5, 1, 2, 5, 10];
  genCount = 5;
  readonly genCounts = [3, 5, 10, 20];
  genSuite: 'core' | 'random' = 'random';
  cases: AiEvalCase[] | null = null;
  showCases = false;
  private casePoll: ReturnType<typeof setTimeout> | null = null;
  reason = '';
  busy = false;
  /** Global switch (migration 192) — preset from the stored state on load. */
  controlMode: AiRoutingMode = 'legacy';
  controlAgentLoop: AiAgentLoopMode = 'legacy';
  controlReason = '';
  taskRuns: AiEvalRun[] = [];

  run: AiEvalRunDetail | null = null;
  runModels: string[] = [];

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private modelsById = new Map<string, AiModel>();

  readonly usd = (v: number | null | undefined, digits = 2): string =>
    v === null || v === undefined
      ? '—'
      : `$${v.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

  constructor(
    private api: TokenAnalyticsService,
    private toastr: NbToastrService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    if (this.casePoll) clearTimeout(this.casePoll);
  }

  load(): void {
    this.isLoading = true;
    this.loadFailed = false;
    this.api.getAiRouting().subscribe({
      next: (d) => this.apply(d),
      error: () => {
        this.isLoading = false;
        this.loadFailed = true;
      },
    });
  }

  private apply(d: AiRoutingOverview): void {
    this.data = d;
    this.isLoading = false;
    this.controlMode = d.control.mode;
    this.controlAgentLoop = d.control.agent_loop;
    this.modelsById = new Map(d.models.map((m) => [m.model, m]));
    this.areas = Array.from(new Set(d.tasks.map((t) => t.area))).sort();
    const keep = this.selected?.task;
    this.selected = keep ? d.tasks.find((t) => t.task === keep) || null : null;
    this.rebuild();
    this.schedulePoll();
  }

  rebuild(): void {
    if (!this.data) return;
    this.visibleTasks = this.data.tasks.filter(
      (t) => (this.area === 'ALL' || t.area === this.area) && (!this.onlyRoutable || t.routable),
    );
    this.rebuildSelection();
  }

  select(task: AiTask): void {
    this.selected = this.selected?.task === task.task ? null : task;
    this.evalPick = new Set<string>();
    this.reason = '';
    this.cases = null;
    this.showCases = false;
    this.rebuildSelection();
  }

  toggleCases(): void {
    this.showCases = !this.showCases;
    if (this.showCases) this.loadCases();
  }

  loadCases(): void {
    if (!this.selected) return;
    const task = this.selected.task;
    this.api.getAiEvalCases(task).subscribe({
      next: (r) => {
        if (this.selected?.task === task) this.cases = r.data;
      },
    });
  }

  /** Generation runs in the background (~10-30 s per case): refresh the list
   *  and the counts a few times instead of waiting on the request. */
  generateCases(): void {
    if (!this.selected) return;
    this.busy = true;
    this.api.generateAiEvalCases(this.selected.task, this.genCount, this.genSuite).subscribe({
      next: () => {
        this.busy = false;
        this.toastr.success(this.translate.instant('tokenAnalytics.routing.casesGenerating', { n: this.genCount }));
        this.showCases = true;
        let rounds = 0;
        const tick = () => {
          this.loadCases();
          this.load();
          if (++rounds < 8) this.casePoll = setTimeout(tick, 15000);
        };
        if (this.casePoll) clearTimeout(this.casePoll);
        this.casePoll = setTimeout(tick, 10000);
      },
      error: (e) => this.fail(e),
    });
  }

  deactivateCase(c: AiEvalCase): void {
    if (c.golden) return;
    this.api.setAiEvalCaseActive(c.id, false).subscribe({
      next: () => {
        this.cases = (this.cases || []).filter((x) => x.id !== c.id);
        this.load();
      },
      error: (e) => this.fail(e),
    });
  }

  caseSetLabel(cs: string): string {
    const [kind, n] = cs.split(':');
    return this.translate.instant(`tokenAnalytics.routing.caseSet.${kind}`, { n });
  }

  experimentList(r: AiEvalRunDetail): { model: string; experiment: string }[] {
    const ex = r.summary?.langsmith?.experiments || {};
    return Object.keys(ex).map((model) => ({ model, experiment: ex[model].experiment }));
  }

  judgeText(ms: { judge?: { better: number; same: number; worse: number } }): string {
    return ms.judge ? `${ms.judge.better} / ${ms.judge.same} / ${ms.judge.worse}` : '—';
  }

  private rebuildSelection(): void {
    const t = this.selected;
    if (!t || !this.data) {
      this.candidateRows = [];
      this.taskRuns = [];
      return;
    }
    // "@legacy" pins the task to its default (legacy) model.
    const active = t.override && !t.override.pinned_legacy ? t.override.model : t.default_model;
    const models = Array.from(new Set([t.default_model, ...t.candidates]));
    this.candidateRows = models
      .map((m) => {
        const info = this.modelsById.get(m);
        const passedRun = t.passed_models[m] || null;
        return {
          model: m,
          backend: info?.backend || '—',
          klass: info?.class || null,
          price: info?.price || null,
          costPerCall: t.cost_per_call_usd[m] ?? null,
          isDefault: m === t.default_model,
          isActive: m === active,
          passedRun,
          // Every switch needs a passed run (function cases or a replay).
          canSwitch: t.routable && m !== active && m !== t.default_model && !!passedRun,
        };
      })
      .sort((a, b) => (a.price?.input ?? 1e9) - (b.price?.input ?? 1e9));
    this.taskRuns = this.data.runs.filter((r) => r.task === t.task);
    const replays = [...(this.data.prod_sampled_allowed ? ['prod_sampled:50'] : []), 'regression:50'];
    this.caseSets = [...(t.has_eval ? this.functionCaseSets : []), ...replays];
    if (!this.caseSets.includes(this.caseSet)) this.caseSet = this.caseSets[0];
  }

  classBelow(model: string | null, requires: AiClass): boolean {
    const k = model ? this.modelsById.get(model)?.class : null;
    return !!k && CLASS_RANK[k] < CLASS_RANK[requires];
  }

  toggleEval(model: string, on: boolean): void {
    if (on) this.evalPick.add(model);
    else this.evalPick.delete(model);
  }

  startEval(): void {
    if (!this.selected || !this.evalPick.size) return;
    this.busy = true;
    this.api.startAiEval(this.selected.task, Array.from(this.evalPick), this.repeats, this.caseSet, this.budgetUsd).subscribe({
      next: (r) => {
        this.busy = false;
        this.evalPick = new Set<string>();
        this.toastr.success(this.translate.instant('tokenAnalytics.routing.evalStarted'));
        this.openRun(r.run_id);
        this.load();
      },
      error: (e) => this.fail(e),
    });
  }

  switchTo(row: CandidateRow): void {
    if (!this.selected) return;
    this.busy = true;
    this.api.setAiRouting(this.selected.task, row.model, this.reason.trim() || null).subscribe({
      next: (d) => {
        this.busy = false;
        this.toastr.success(this.translate.instant('tokenAnalytics.routing.switched', { model: row.model }));
        this.apply(d);
      },
      error: (e) => this.fail(e),
    });
  }

  reset(): void {
    if (!this.selected) return;
    this.busy = true;
    this.api.resetAiRouting(this.selected.task).subscribe({
      next: (d) => {
        this.busy = false;
        this.toastr.success(this.translate.instant('tokenAnalytics.routing.resetDone'));
        this.apply(d);
      },
      error: (e) => this.fail(e),
    });
  }

  /** Global switch: mode legacy = today's system for every task, within seconds. */
  applyControl(): void {
    const reason = this.controlReason.trim();
    if (reason.length < 3) {
      this.toastr.warning(this.translate.instant('tokenAnalytics.routing.control.reasonRequired'));
      return;
    }
    this.busy = true;
    this.api.setAiRoutingControl({ mode: this.controlMode, agent_loop: this.controlAgentLoop }, reason).subscribe({
      next: (d) => {
        this.busy = false;
        this.controlReason = '';
        this.toastr.success(this.translate.instant('tokenAnalytics.routing.control.applied', { rev: d.control.rev }));
        this.apply(d);
      },
      error: (e) => this.fail(e),
    });
  }

  /** The emergency action: everything back to today's system in one step. */
  allLegacy(): void {
    this.controlMode = 'legacy';
    this.controlAgentLoop = 'legacy';
    if (this.controlReason.trim().length < 3) {
      this.controlReason = this.translate.instant('tokenAnalytics.routing.control.allLegacyReason');
    }
    this.applyControl();
  }

  /** Per-task rollback: this task back on its legacy model, the rest unchanged. */
  pinLegacy(): void {
    if (!this.selected) return;
    const reason = this.reason.trim();
    if (reason.length < 3) {
      this.toastr.warning(this.translate.instant('tokenAnalytics.routing.control.reasonRequired'));
      return;
    }
    this.busy = true;
    this.api.pinAiRoutingLegacy(this.selected.task, reason).subscribe({
      next: (d) => {
        this.busy = false;
        this.reason = '';
        this.toastr.success(this.translate.instant('tokenAnalytics.routing.pinned'));
        this.apply(d);
      },
      error: (e) => this.fail(e),
    });
  }

  openRun(runId: string): void {
    this.api.getAiEval(runId).subscribe({
      next: (r) => {
        this.run = r;
        this.runModels = r.summary ? Object.keys(r.summary.models) : r.models;
      },
    });
  }

  closeRun(): void {
    this.run = null;
  }

  private fail(e: any): void {
    this.busy = false;
    const detail = e?.error?.detail;
    this.toastr.danger(typeof detail === 'string' ? detail : this.translate.instant('tokenAnalytics.routing.actionError'));
  }

  // Laufende Vergleichsläufe: alle 5 s neu laden, bis keiner mehr läuft.
  private schedulePoll(): void {
    this.stopPolling();
    const running = this.data?.runs.some((r) => r.status === 'running');
    if (!running) return;
    this.pollTimer = setTimeout(() => {
      this.api.getAiRouting().subscribe({ next: (d) => this.apply(d) });
      if (this.run?.status === 'running') this.openRun(this.run.id);
    }, 5000);
  }

  private stopPolling(): void {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  trackTask(_: number, t: AiTask): string {
    return t.task;
  }

  trackModel(_: number, r: { model: string }): string {
    return r.model;
  }

  trackRun(_: number, r: AiEvalRun): string {
    return r.id;
  }
}
